import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { KPI_TOOLTIP_DELAY } from "@/modules/boxDashboard/modules/KPI/components/kpiBlock"
import { VariationBadge } from "@/modules/boxDashboard/modules/KPI/components/variationBadge"
import type {
  ConversionRateModel,
  TrialsCountModel,
  TrialsKpiModel,
} from "@/modules/boxDashboard/modules/KPI/modules/trials/models/trialsKpiModel"
import { word } from "@modules/core/utils/i18n"
import { Flag, Percent } from "lucide-react"

type TrialsKpiBlockProps = {
  model: TrialsKpiModel
}

export function TrialsKpiBlock({ model }: TrialsKpiBlockProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Text fontStyle="TM" className="font-semibold text-textOnSurface-default">
          {word("boxDashboard.kpi.trialSessions.title")}
        </Text>
        <Text fontStyle="LS" className="text-textOnSurface-muted">
          {word("boxDashboard.kpi.trialSessions.subtitle")}
        </Text>
      </div>

      <div className="flex gap-4">
        <TrialsCard
          icon={<Flag size={32} color="currentColor" className="text-textOnSurface-subtle" />}
          title={word("boxDashboard.kpi.trialSessions.trials.title")}
          tooltip={word("boxDashboard.kpi.trialSessions.trials.tooltip")}
          trials={model.trials}
        />
        <ConversionRateCard
          icon={<Percent size={32} color="currentColor" className="text-textOnSurface-subtle" />}
          title={word("boxDashboard.kpi.trialSessions.conversionRate.title")}
          tooltip={word("boxDashboard.kpi.trialSessions.conversionRate.tooltip")}
          conversionRate={model.conversionRate}
        />
      </div>
    </div>
  )
}

type TrialsCardProps = {
  icon: React.ReactNode
  title: string
  tooltip: string
  trials: TrialsCountModel
}

function TrialsCard({ icon, title, tooltip, trials }: TrialsCardProps) {
  return (
    <TooltipProvider delay={KPI_TOOLTIP_DELAY}>
      <Tooltip>
        <TooltipTrigger
          className="cursor-help"
          render={
            <div className="flex flex-1 flex-col gap-2 rounded-xl bg-surface-background p-4">
              <div className="flex items-center gap-2">{icon}</div>

              <div className="flex flex-col gap-1">
                <Text fontStyle="LM" className="font-semibold text-textOnSurface-muted">
                  {title}
                </Text>

                <div className="flex items-center gap-2">
                  <Text fontStyle="HM" className="text-textOnSurface-default">
                    {trials.count === 0 && [0, null].includes(trials.comparisonCount) ? "–" : trials.count}
                  </Text>
                  {trials.comparisonCount !== null && trials.variationDirection !== null && (
                    <VariationBadge
                      value={trials.comparisonCount}
                      direction={trials.variationDirection}
                      type="points"
                    />
                  )}
                </div>
              </div>
            </div>
          }
        />
        <TooltipContent className="max-w-80">
          <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
            {tooltip}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type ConversionRateCardProps = {
  icon: React.ReactNode
  title: string
  tooltip: string
  conversionRate: ConversionRateModel
}

function ConversionRateCard({ icon, title, tooltip, conversionRate }: ConversionRateCardProps) {
  return (
    <TooltipProvider delay={KPI_TOOLTIP_DELAY}>
      <Tooltip>
        <TooltipTrigger
          className="cursor-help"
          render={
            <div className="flex flex-1 flex-col gap-2 rounded-xl bg-surface-background p-4">
              <div className="flex items-center gap-2">{icon}</div>

              <div className="flex flex-col gap-1">
                <Text fontStyle="LM" className="font-semibold text-textOnSurface-muted">
                  {title}
                </Text>

                <div className="flex items-center gap-2">
                  <Text fontStyle="HM" className="text-textOnSurface-default">
                    {conversionRate.rate === 0 && conversionRate.comparisonRate === null
                      ? "– "
                      : `${conversionRate.rate}`}
                    <Text fontStyle="TL" className="text-textOnSurface-default">
                      %
                    </Text>
                  </Text>
                  {conversionRate.variationPoints !== null && conversionRate.variationDirection !== null && (
                    <VariationBadge
                      value={conversionRate.variationPoints}
                      direction={conversionRate.variationDirection}
                      type="percent"
                    />
                  )}
                </div>
              </div>
            </div>
          }
        />
        <TooltipContent className="max-w-80">
          <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
            {tooltip}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
